import {WHATS_NEW_SEO} from '../../whats-new/whats-new-seo';
import {LANDING_SEO} from '../../landing/landing-seo';
import {PublicSeoMetadata} from './public-seo-metadata';
export type {PublicSeoMetadata} from './public-seo-metadata';
import {DOCUMENT} from '@angular/common';
import {Inject, Injectable, OnDestroy} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {filter, Subscription} from 'rxjs';
import {environment} from '../../../environments/environment';
import {PUBLIC_GUIDE_METADATA} from '../../guides/public-guide-metadata';
import {
  AppLanguageCode,
  buildLocalizedRoutePathname,
  extractLocalizedRouteLanguageFromPathname,
  stripLocalizedRouteLanguageFromPathname,
  SUPPORTED_APP_LANGUAGE_CODES,
} from '../i18n/app-language';
import {AppLanguageService} from '../i18n/app-language.service';

const DEFAULT_DESCRIPTION = 'Organize your things, label them with QR codes, and find them in seconds with Invemory.';
const DEFAULT_TITLE = 'Invemory';

const LOCALIZED_STATIC_PATHNAMES = new Set([
  '/',
  '/about',
  '/whats-new',
  '/terms',
  '/privacy',
  '/account-deletion',
]);

function normalizeSiteUrl(siteUrl: string): string {
  return siteUrl.trim().replace(/\/+$/, '');
}

function normalizePathname(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/';
}

export function buildPublicSeoMetadata(
  routeUrl: string,
  fallbackLanguage: AppLanguageCode,
  siteUrl = environment.webAppBaseUrl,
): PublicSeoMetadata | null {
  const pathname = normalizePathname(new URL(routeUrl, 'http://localhost').pathname);
  const routeLanguage = extractLocalizedRouteLanguageFromPathname(pathname);
  const routePathname = normalizePathname(stripLocalizedRouteLanguageFromPathname(pathname));
  const origin = normalizeSiteUrl(siteUrl);

  if (LOCALIZED_STATIC_PATHNAMES.has(routePathname)) {
    const language = routeLanguage ?? fallbackLanguage;
    return {
      ...(routePathname === '/whats-new' ? WHATS_NEW_SEO[language] : {}),
      ...(routePathname === '/' ? LANDING_SEO[language] : {}),
      canonicalUrl: `${origin}${buildLocalizedRoutePathname(routePathname, language)}`,
      alternateUrls: SUPPORTED_APP_LANGUAGE_CODES.map(alternateLanguage => ({
        language: alternateLanguage,
        url: `${origin}${buildLocalizedRoutePathname(routePathname, alternateLanguage)}`,
      })),
    };
  }

  const guideMetadata = Object.prototype.hasOwnProperty.call(PUBLIC_GUIDE_METADATA, routePathname) ? PUBLIC_GUIDE_METADATA[routePathname] : undefined;
  if (routeLanguage && guideMetadata) {
    const content = guideMetadata[routeLanguage];
    return {
      canonicalUrl: `${origin}/${routeLanguage}${routePathname}`,
      alternateUrls: SUPPORTED_APP_LANGUAGE_CODES.map(language => ({language, url: `${origin}/${language}${routePathname}`})),
      description: content.metaDescription,
      title: content.metaTitle,
    };
  }

  if (routePathname === '/items' || /^\/items\/[^/]+\/[^/]+$/.test(routePathname)) {
    return {
      canonicalUrl: `${origin}${routePathname}`,
      alternateUrls: [],
    };
  }

  return null;
}

@Injectable({providedIn: 'root'})
export class PublicSeoMetadataService implements OnDestroy {
  private readonly subscriptions = new Subscription();
  private initialized = false;
  private customPageMetadataApplied = false;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly appLanguageService: AppLanguageService,
    private readonly router: Router,
    private readonly translateService: TranslateService,
  ) {}

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.apply(this.router.url);
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(event => this.apply(event.urlAfterRedirects)),
    );
    this.subscriptions.add(
      this.translateService.onLangChange.subscribe(() => {
        this.apply(this.document.location?.pathname || this.router.url);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  apply(routeUrl: string): void {
    const metadata = buildPublicSeoMetadata(
      routeUrl,
      this.appLanguageService.languageCode,
    );

    this.removeManagedLinks();
    if (!metadata) {
      if (this.customPageMetadataApplied) {
        this.updatePageMetadata(DEFAULT_TITLE, DEFAULT_DESCRIPTION);
        this.setMetaContent('meta[property="og:url"]', `${normalizeSiteUrl(environment.webAppBaseUrl)}/`);
        this.customPageMetadataApplied = false;
      }
      return;
    }

    this.appendLink('canonical', metadata.canonicalUrl);
    for (const alternate of metadata.alternateUrls) {
      this.appendLink('alternate', alternate.url, alternate.language);
    }

    const openGraphUrl = this.document.head.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    openGraphUrl?.setAttribute('content', metadata.canonicalUrl);

    this.updatePageMetadata(
      metadata.title ?? DEFAULT_TITLE,
      metadata.description ?? DEFAULT_DESCRIPTION,
    );
    this.customPageMetadataApplied = Boolean(metadata.title || metadata.description);
  }

  private updatePageMetadata(title: string, description: string): void {
    this.document.title = title;
    this.setMetaContent('meta[name="description"]', description);
    this.setMetaContent('meta[property="og:title"]', title);
    this.setMetaContent('meta[property="og:description"]', description);
    this.setMetaContent('meta[name="twitter:title"]', title);
    this.setMetaContent('meta[name="twitter:description"]', description);
  }

  private setMetaContent(selector: string, content: string): void {
    this.document.head.querySelector<HTMLMetaElement>(selector)?.setAttribute('content', content);
  }

  private removeManagedLinks(): void {
    this.document.head.querySelector('link[rel="canonical"]')?.remove();
    this.document.head
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach(link => link.remove());
  }

  private appendLink(rel: string, href: string, hreflang?: string): void {
    const link = this.document.createElement('link');
    link.setAttribute('rel', rel);
    link.setAttribute('href', href);
    if (hreflang) {
      link.setAttribute('hreflang', hreflang);
    }
    this.document.head.appendChild(link);
  }
}
